import { CSSProperties } from 'react'
import styled, { css } from 'styled-components'
import { LineHeightProps, SpaceProps } from 'styled-system'
import { variant } from 'styled-system'

import Box from 'components/Box'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import Text from 'components/Text'
import Tooltip from 'components/Tooltip'

import { WidthProps } from 'types/styled'
import { ThemeColor } from 'utils/colors'

import * as variants from './variants'

export type BadgeColor = keyof typeof variants

type TextTransform = 'capitalize' | 'lowercase' | 'none' | 'uppercase'

type Size = 'sm' | 'md' | 'lg'

type BadgeProps = SpaceProps &
  LineHeightProps & {
    className?: string
    color?: BadgeColor
    icon?: string | string[]
    name?: string
    role?: string
    text?: string
    textColor?: ThemeColor
    textTransform?: TextTransform
    // Used to show focusable badge styles. e.g. border on hover
    isFocusable?: boolean
    isLoading?: boolean
    // Tooltip props
    tooltip?: string
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'topRight' | 'topLeft' | 'bottomLeft'
    tooltipTextTransform?: TextTransform
    tooltipTextWidth?: WidthProps | 'max-content'
    size?: Size
    fontSize?: number
    style?: CSSProperties
  }

const BadgeV2 = ({
  className,
  color = 'gray',
  icon,
  text,
  textColor,
  isFocusable = false,
  isLoading = false,
  textTransform,
  tooltip,
  tooltipPosition,
  tooltipTextTransform = 'none',
  tooltipTextWidth = 'max-content',
  size = 'md',
  fontSize = 14,
  style,
  role = 'status',
  ...spaceProps
}: BadgeProps) => {
  const txtColor: ThemeColor = isLoading
    ? 'transparent'
    : (textColor || (variants[color].color as ThemeColor)) ?? 'defaultText'
  const render = (
    <Box {...spaceProps} role={role}>
      <BaseBadge
        className={className}
        variant={color}
        icon={icon}
        isFocusable={isFocusable}
        text={text}
        textTransform={textTransform}
        size={size}
        isLoading={isLoading}
        style={{ ...style }}
      >
        {icon && <StyledIcon icon={icon} color={txtColor} />}
        {text && (
          <Text
            color={txtColor}
            lineHeight={spaceProps.lineHeight}
            fontSize={fontSize}
            textTransform={textTransform}
            isCapitalizedFirstLetter
          >
            {text}
          </Text>
        )}
        {isLoading && (
          <LoadingOverlay>
            <Spinner fontSize={fontSize} />
          </LoadingOverlay>
        )}
      </BaseBadge>
    </Box>
  )

  if (tooltip) {
    return (
      <Tooltip
        title={tooltip}
        position={tooltipPosition}
        textTransform={tooltipTextTransform}
        textWidth={tooltipTextWidth}
      >
        {render}
      </Tooltip>
    )
  }

  return render
}

const getMinHeight = (size: Size) => {
  switch (size) {
    case 'sm':
      return '1.2rem'
    case 'lg':
      return '2rem'
    default:
      return '1.5rem'
  }
}

const BaseBadge = styled.span<{
  variant: BadgeColor
  icon?: string | string[]
  isFocusable: boolean
  text?: string
  textTransform?: TextTransform
  size: Size
  isLoading: boolean
}>((p) =>
  css(
    {
      boxSizing: 'border-box',
      borderRadius: '4px',
      minHeight: getMinHeight(p.size),
      height: '100%',
      minWidth: p.icon && p.text ? '105px' : p.icon ? '32px' : p.size === 'sm' ? '20px' : '54px',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: p.size === 'sm' ? '0.2rem' : '2px 8px',
      color: p.isLoading ? 'transparent' : undefined,
      ':hover': {
        outline: p.isFocusable ? '1px solid' : undefined,
        cursor: p.isLoading ? 'progress' : p.isFocusable ? 'pointer' : undefined,
        boxShadow: p.isFocusable ? `0 0 0 4px ${p.theme.colors.gray2}` : undefined,
      },
    },
    variant({ variants }),
  ),
)

const StyledIcon = styled((p) => <Icon {...p} />)<{ withText?: boolean }>`
  padding-right: ${(p) => (p.withText ? '6px' : 'none')};
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`

export default BadgeV2
